<template>
  <modal-wrapper
    ref="modalRef"
    :busy="isCreating"
    :close-on-ok-click="false"
    :display-footer="!token"
    :header="t('supplierAuthTokens.createAuthToken')"
    :ok-button-label="t('generic.create')"
    @form-submit="createToken"
    @hide="resetForm"
    @ok-button-click="submitForm"
  >
    <create-supplier-token-form
      v-if="!token"
      ref="formRef"
      @submit="createToken"
    />
    <supplier-auth-token-display v-else :token="token" />
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useModalWrapper } from '../ModalWrapper';
  import CreateSupplierTokenForm from '../CreateSupplierTokenForm/CreateSupplierTokenForm.vue';
  import axios from 'axios';
  import { endpointsList } from '@/helpers';
  import { ICreateSupplierAuthTokenDto } from 'types';
  import { FormSubmitEvent } from '@primevue/forms/form';
  import { SupplierTokenFormData } from '../CreateSupplierTokenForm/SupplierTokenForm.types';
  import { useToast } from '@/composables/useToast';

  const props = defineProps<{
    supplierId: string;
  }>();

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();
  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const emit = defineEmits<{
    (e: 'hide'): void;
  }>();

  const isCreating = ref(false);
  const formRef = ref<InstanceType<typeof CreateSupplierTokenForm>>();
  const token = ref<string>('');

  function submitForm(): void {
    formRef.value?.submit();
  }

  function resetForm(): void {
    token.value = '';
    emit('hide');
  }

  async function createToken(
    event: FormSubmitEvent<SupplierTokenFormData>,
  ): Promise<void> {
    isCreating.value = true;
    try {
      const body: ICreateSupplierAuthTokenDto = {
        supplierId: props.supplierId,
        name: event.values.name,
        expiresInDays: event.values.expiresInDays,
      };
      const response = await axios.post<string>(
        endpointsList.supplierAuthTokens.createToken(),
        body,
      );
      token.value = response.data;
      createSuccessToast(t('supplierAuthTokens.tokenCreated'));
    } catch {
      createErrorToast(t('supplierAuthTokens.errors.errorCreatingToken'));
    } finally {
      isCreating.value = false;
    }
  }
</script>
